import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import Icon from 'components/DesignSystem/Icon';
import BrazilStatesLayer from 'components/Meteorology/Agro/BrazilStatesLayer';
import { store } from 'redux/store';
import {
  setCreateAlert,
  setEditAlert,
  selectMeteorologyAgroGroupLocationCreateAlert,
  selectMeteorologyAgroGroupLocationEditAlert,
} from 'redux/reducers/meteorologyAgroGroupLocationFeedbackReducer';
import useLocationMarkers from 'hooks/Meteorology/Agro/useLocationMarkers';
import useLocationGroups from 'hooks/Meteorology/Agro/useLocationGroups';
import useLocations from 'hooks/Meteorology/Agro/useLocations';
import useCities from 'hooks/Meteorology/Agro/useCities';
import useSaveLocationGroup from 'hooks/Meteorology/Agro/useSaveLocationGroup';
import useUpdateLocationGroup from 'hooks/Meteorology/Agro/useUpdateLocationGroup';
import useUpdateLocation from 'hooks/Meteorology/Agro/useUpdateLocation';
import useDeleteLocationGroup from 'hooks/Meteorology/Agro/useDeleteLocationGroup';
import useDeleteLocation from 'hooks/Meteorology/Agro/useDeleteLocation';
import useAgroSaveLocationParams from 'hooks/Meteorology/Agro/RegisterForm/useAgroSaveLocationParams';
import { cssVar } from 'constants/theme';

import RegisterSheet from 'components/Meteorology/Shared/RegisterMobile/RegisterSheet';
import EditSheet from 'components/Meteorology/Shared/RegisterMobile/EditSheet';

import 'components/Meteorology/Shared/RegisterMobile/index.scss';

export default function RegisterMobile() {
  const markers = useLocationMarkers();
  const { groups } = useLocationGroups();
  const { locations } = useLocations();
  const { cities } = useCities();
  const { saveGroup } = useSaveLocationGroup();
  const { saveLocationParams } = useAgroSaveLocationParams();
  const { updateGroup } = useUpdateLocationGroup();
  const { updateLocation } = useUpdateLocation();
  const { deleteGroup } = useDeleteLocationGroup();
  const { deleteLocation } = useDeleteLocation();

  const alertRegister = useSelector(selectMeteorologyAgroGroupLocationCreateAlert);
  const alertEdit = useSelector(selectMeteorologyAgroGroupLocationEditAlert);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const closeRegister = useCallback(() => {
    setRegisterOpen(false);
    store.dispatch(setCreateAlert(undefined));
  }, []);
  const closeEdit = useCallback(() => {
    setEditOpen(false);
    store.dispatch(setEditAlert(undefined));
  }, []);

  const groupCount = groups?.length ?? 0;
  const locationCount = locations?.length ?? 0;
  const summaryLabel = `${groupCount} grupo${groupCount === 1 ? '' : 's'} · ${locationCount} ${locationCount === 1 ? 'local' : 'locais'}`;

  return (
    <div className="register-mobile agro-register-mobile">
      <div className="register-mobile-map-card">
        <button
          type="button"
          className="register-mobile-edit-button"
          onClick={() => setEditOpen(true)}
        >
          <Icon iconType="edit" color={cssVar('text-strong')} size="small" />
          Editar
        </button>
        <InteractiveMap
          markers={markers}
          minZoom={3}
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        >
          <BrazilStatesLayer />
        </InteractiveMap>
      </div>

      <div className="register-mobile-summary-card">
        <div className="register-mobile-summary-header">
          <span className="register-mobile-summary-title">Grupos e locais</span>
          <span className="register-mobile-summary-count">{summaryLabel}</span>
        </div>
        {groups?.map((group) => {
          const groupLocations = locations?.filter((l) => l.groupId === group.id) || [];
          const chipLabel = groupLocations.length === 1
            ? groupLocations[0].name
            : `${groupLocations.length} locais`;
          return (
            <div key={group.id} className="register-mobile-summary-row">
              <span className="register-mobile-summary-icon">
                <Icon iconType="list" color={cssVar('text-strong')} size="small" />
              </span>
              <div className="register-mobile-summary-info">
                <span className="register-mobile-summary-name">{group.name}</span>
                <span className="register-mobile-summary-sub">
                  {`Grupo · ${groupLocations.length} ${groupLocations.length === 1 ? 'local' : 'locais'}`}
                </span>
              </div>
              <span className="register-mobile-summary-chip">{chipLabel}</span>
            </div>
          );
        })}
        {groupCount === 0 && (
          <p className="register-mobile-summary-empty">Nenhum grupo cadastrado ainda.</p>
        )}
      </div>

      <button
        type="button"
        className="register-mobile-fab"
        onClick={() => setRegisterOpen(true)}
      >
        <Icon iconType="add" color={cssVar('text-on-accent')} size="small" />
        Cadastrar
      </button>

      <RegisterSheet
        open={registerOpen}
        dismiss={closeRegister}
        idPrefix="agro"
        alert={alertRegister}
        groups={groups}
        cities={cities}
        saveGroup={saveGroup}
        saveLocationParams={saveLocationParams}
      />
      <EditSheet
        open={editOpen}
        dismiss={closeEdit}
        alert={alertEdit}
        groups={groups}
        locations={locations}
        updateGroup={updateGroup}
        updateLocation={updateLocation}
        deleteGroup={deleteGroup}
        deleteLocation={deleteLocation}
      />
    </div>
  );
}
